/**
 * What App.tsx hands `configureAuth` — here, not inline, so it can be tested.
 *
 * `authApiUrl` is shared-auth's API WITHOUT its version (#1316). The package
 * appends `/v1/me/profile` to it for the Welcome step's name save
 * (`updateUserAttributes({ fullName })`, shared-react-auth 0.53.0) and
 * `/v1/me/sessions/revoke` for sign-out-everywhere, while `AUTH_API_URL`
 * carries the version for the routes the sign-up page calls
 * (`/verification/send`). Without `authApiUrl` the name save falls back to a
 * direct Cognito write of `custom:fullName`, which skips the name rule and is
 * refused once shared-auth takes the attribute off the SystemDox app client's
 * WriteAttributes (shared-auth#679).
 *
 * No `tokenStorage`: the tokens stay where they are — the `.systemdox.com`
 * cookie when REACT_APP_COOKIE_DOMAIN is set, Amplify's default otherwise.
 */
import type { AuthConfig } from '@puglieseweb/shared-react-auth';
import { AUTH_API_URL } from '@/lib/apiConfig';
import { APP_ORIGIN, signsInThroughAppHost } from '@/utils/subdomain';

/** The build variables this reads (`import.meta.env`, which carries others too). */
export interface AuthEnv {
  readonly [key: string]: unknown;
  REACT_APP_USER_POOL_ID?: string;
  REACT_APP_CLIENT_ID?: string;
  REACT_APP_COGNITO_DOMAIN?: string;
  REACT_APP_COOKIE_DOMAIN?: string;
}

/** `https://api.puglieseweb.com/auth/v1` → `https://api.puglieseweb.com/auth`. */
export function authApiBase(url: string): string {
  return url.replace(/\/v1\/?$/, '');
}

export function authConfig(
  env: AuthEnv = import.meta.env,
  authApiUrl: string = AUTH_API_URL,
  hostname: string = typeof window === 'undefined' ? '' : window.location.hostname,
): AuthConfig {
  const cognitoDomain = env.REACT_APP_COGNITO_DOMAIN;
  // A workspace host is not a registered sign-out address, so signing out
  // there lands on the app host (utils/subdomain › signsInThroughAppHost).
  // Amplify needs the URL in `redirectSignOut` as well, beside this origin.
  const viaAppHost = Boolean(cognitoDomain) && signsInThroughAppHost(hostname);
  const cookieDomain = env.REACT_APP_COOKIE_DOMAIN;
  return {
    userPoolId: env.REACT_APP_USER_POOL_ID || '',
    userPoolClientId: env.REACT_APP_CLIENT_ID || '',
    tokenType: 'id',
    authApiUrl: authApiBase(authApiUrl),
    ...(cookieDomain && { cookieDomain }),
    // [ADR-061] §8, C2: with the hosted-UI domain configured, sign-in is the
    // pool's managed login by top-level redirect — the one path that also
    // creates the pool session single sign-on across the products consumes.
    // Derived from the same condition as `oauth`: `frontDoor: 'hosted'` without
    // `oauth` throws at startup, and an unset build variable must not.
    ...(cognitoDomain && {
      frontDoor: 'hosted' as const,
      oauth: {
        domain: cognitoDomain,
        providers: ['Google', 'Apple'] as const,
        // The three defaults plus `aws.cognito.signin.user.admin` ([ADR-121]).
        // A token minted by the hosted page carries only the scopes asked for
        // here, and the MFA setup page (AssociateSoftwareToken) is refused
        // without that one: "Access Token does not have required scopes"
        // (owner, 2026-10-25). 🚨 shared-auth's SystemDoxAppClient must ALLOW it
        // first (shared-auth#854), or the hosted page answers invalid_scope and
        // nobody can sign in.
        scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
        ...(viaAppHost && { redirectSignOut: [`https://${hostname}`, APP_ORIGIN] }),
      },
      ...(viaAppHost && { signOutRedirectUrl: APP_ORIGIN }),
    }),
  };
}
